import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Redirect, useHistory } from "react-router-dom";
import {
  FaEdit,
  FaIdCard,
  FaLayerGroup,
  FaSchool,
  FaShareAlt,
  FaStar,
  FaSyncAlt,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/school-profile.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const LOGO_ACCEPT = "image/*";
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

// Created By Cursor
function parseUploadApiResponse(data) {
  if (data == null) return null;
  if (typeof data === "object" && !Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

// Created By Cursor
function withCacheBust(url) {
  const raw = String(url || "").trim();
  if (!raw) return raw;
  const base = raw.split("?")[0];
  return `${base}?v=${Date.now()}`;
}

// Created By Cursor
function displayValue(value) {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "—";
  }
  return String(value);
}

// Created By Cursor
function formatPhone(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  if (raw.startsWith("+")) return raw;
  return `+91 ${raw}`;
}

// Created By Cursor
function websiteHref(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

// Created By Cursor — compact inline school detail row
function CompactField({ label, value, href }) {
  const empty = displayValue(value) === "—";
  return (
    <div className="sp-compact__item">
      <span className="sp-compact__label">{label}</span>
      {href && !empty ? (
        <a
          className="sp-compact__value"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {displayValue(value)}
        </a>
      ) : (
        <span
          className={`sp-compact__value${
            empty ? " sp-compact__value--empty" : ""
          }`}
        >
          {displayValue(value)}
        </span>
      )}
    </div>
  );
}

export default function SchoolProfile() {
  const history = useHistory();
  const logoInputRef = useRef(null);
  const [loggedIn] = useState(!!localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [school, setSchool] = useState(null);
  const [logoBroken, setLogoBroken] = useState(false);
  const [affiliations, setAffiliations] = useState([]);

  // Created By Cursor
  const fetchAffiliations = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    const sessionId = SessionManager.getCurrentSession()?.[0]?.session_id;
    if (!schoolCode || !sessionId) {
      setAffiliations([]);
      return;
    }
    try {
      const { data } = await axios.get(`${API_BASE()}getSchoolAffiliations.php`, {
        params: { school_code: schoolCode, session_id: sessionId },
      });
      if (data?.api_status === "OK") {
        setAffiliations(Array.isArray(data.data) ? data.data : []);
      } else {
        setAffiliations([]);
      }
    } catch {
      setAffiliations([]);
    }
  }, []);

  // Created By Cursor
  const fetchSchoolProfile = useCallback(async ({ soft = false } = {}) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_error("School code is missing. Please login again.");
      setLoading(false);
      return;
    }

    if (soft) setRefreshing(true);
    else setLoading(true);

    try {
      const { data } = await axios.get(`${API_BASE()}getSchoolProfile.php`, {
        params: { school_code: schoolCode },
      });

      if (data?.api_status === "OK") {
        const row = Array.isArray(data.data) ? data.data[0] : data.data;
        if (row?.school_logo_path) {
          row.school_logo_path = soft
            ? withCacheBust(row.school_logo_path)
            : row.school_logo_path;
        }
        setSchool(row || null);
        setLogoBroken(false);
      } else {
        setSchool(null);
        myToast.notify_warning(data?.api_remark || "Failed to load school profile");
      }
    } catch (error) {
      setSchool(null);
      myToast.notify_error(
        error?.response?.data?.api_remark || "Network error. Please check your connection."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSchoolProfile();
    fetchAffiliations();
  }, [fetchSchoolProfile, fetchAffiliations]);

  const heroChips = useMemo(() => {
    if (!school) return [];
    return [
      school.school_code ? `School Code ${school.school_code}` : null,
      school.school_estb_year ? `Est. ${school.school_estb_year}` : null,
      school.udise_no ? `UDISE ${school.udise_no}` : null,
    ].filter(Boolean);
  }, [school]);

  // Created By Cursor
  const handleEdit = useCallback(() => {
    if (!school) return;
    history.push({
      pathname: "/AdminMain/UpdateSchoolDetails",
      state: { school },
    });
  }, [history, school]);

  // Created By Cursor — native label opens picker instantly (avoid JS .click + heavy accept)
  const onLogoLabelClick = useCallback(
    (e) => {
      if (uploadingLogo) {
        e.preventDefault();
      }
    },
    [uploadingLogo]
  );

  // Created By Cursor
  const handleLogoSelected = useCallback(
    async (event) => {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_error("School code is missing. Please login again.");
        return;
      }

      const nameExt = String(file.name || "")
        .split(".")
        .pop()
        .toLowerCase();
      const okExt = ["jpg", "jpeg", "png", "gif", "webp"].includes(nameExt);
      const okType =
        !file.type ||
        String(file.type).startsWith("image/") ||
        file.type === "application/octet-stream";
      if (!okExt && !okType) {
        myToast.notify_warning(
          "Please choose an image file (JPG, PNG, GIF, or WEBP)."
        );
        return;
      }
      if (file.size > LOGO_MAX_BYTES) {
        myToast.notify_warning("Logo must be 2 MB or smaller.");
        return;
      }

      const formData = new FormData();
      formData.append("image", file, file.name || `logo.${nameExt || "png"}`);
      formData.append("school_code", schoolCode);

      setUploadingLogo(true);
      try {
        const response = await axios.post(
          `${API_BASE()}uploadSchoolLogo.php`,
          formData
        );
        const payload = parseUploadApiResponse(response.data);
        if (payload?.api_status === "OK") {
          const nextPath =
            payload.school_logo_path ||
            withCacheBust(school?.school_logo_path) ||
            null;
          if (nextPath) {
            setSchool((prev) =>
              prev
                ? { ...prev, school_logo_path: withCacheBust(nextPath) }
                : prev
            );
            setLogoBroken(false);
          }
          myToast.notify_success(
            payload.api_remark || "Logo updated successfully!"
          );
          fetchSchoolProfile({ soft: true });
        } else {
          myToast.notify_warning(
            (payload && (payload.api_remark || payload.message)) ||
              "Logo upload failed"
          );
        }
      } catch (error) {
        console.error("School logo upload error:", error);
        const errPayload = parseUploadApiResponse(error.response?.data);
        const raw =
          typeof error.response?.data === "string"
            ? error.response.data.slice(0, 120)
            : "";
        myToast.notify_warning(
          (errPayload && (errPayload.api_remark || errPayload.message)) ||
            raw ||
            "Logo upload failed."
        );
      } finally {
        setUploadingLogo(false);
      }
    },
    [fetchSchoolProfile, school?.school_logo_path]
  );

  // Created By Cursor
  const handleShare = useCallback(async () => {
    if (!school) return;
    const text = [
      school.school_name,
      school.school_tag_line,
      school.school_address,
      school.officail_contact_no1
        ? `Contact: ${formatPhone(school.officail_contact_no1)}`
        : null,
      school.officail_email ? `Email: ${school.officail_email}` : null,
      school.website ? `Web: ${school.website}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    try {
      if (navigator.share) {
        await navigator.share({
          title: school.school_name || "School Profile",
          text,
        });
        return;
      }
      await navigator.clipboard.writeText(text);
      myToast.notify_success("School profile copied");
    } catch (error) {
      if (error?.name === "AbortError") return;
      myToast.notify_warning("Unable to share school profile");
    }
  }, [school]);

  if (!loggedIn) {
    return <Redirect to="/" />;
  }

  if (loading) {
    return <MyLoader />;
  }

  return (
    <div
      className="content payroll-page sm-page sp-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> School Profile</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell sp-shell">
        <header className="sm-hero sp-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div
              className={`sp-hero__logo-wrap${
                uploadingLogo ? " is-uploading" : ""
              }`}
            >
              {school?.school_logo_path && !logoBroken ? (
                <img
                  src={school.school_logo_path}
                  alt={school.school_name || "School logo"}
                  className="sp-hero__logo"
                  onError={() => setLogoBroken(true)}
                />
              ) : (
                <span className="sp-hero__logo sp-hero__logo--fallback" aria-hidden>
                  <FaSchool />
                </span>
              )}
              <label
                htmlFor="sp-school-logo-file"
                className={`sp-hero__upload-btn${
                  uploadingLogo ? " is-busy" : ""
                }`}
                onClick={onLogoLabelClick}
                aria-disabled={uploadingLogo}
              >
                {uploadingLogo ? "Uploading…" : "Upload logo"}
              </label>
              <input
                id="sp-school-logo-file"
                ref={logoInputRef}
                type="file"
                accept={LOGO_ACCEPT}
                onChange={handleLogoSelected}
                disabled={uploadingLogo}
                tabIndex={-1}
              />
            </div>
            <div className="sp-hero__text">
              <p className="sp-hero__eyebrow">School profile</p>
              <h1 className="sm-hero__title">
                {displayValue(school?.school_name) === "—"
                  ? "School Profile"
                  : school.school_name}
              </h1>
              <p className="sm-hero__sub">
                {displayValue(school?.school_tag_line) === "—"
                  ? "Institution details, contacts & affiliations"
                  : school.school_tag_line}
              </p>
              {heroChips.length > 0 ? (
                <div className="sp-hero__chips">
                  {heroChips.map((chip) => (
                    <span key={chip} className="sp-hero__chip">
                      {chip}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn sp-refresh${refreshing ? " spinning" : ""}`}
              onClick={() => {
                fetchSchoolProfile({ soft: true });
                fetchAffiliations();
              }}
              disabled={refreshing}
              aria-label="Refresh school profile"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        {!school ? (
          <div className="sp-empty">No school profile found for this account.</div>
        ) : (
          <div className="sp-body">
            <section className="sp-card sp-compact sp-reveal">
              <header className="sp-card__head">
                <span className="sp-card__icon" aria-hidden>
                  <FaIdCard />
                </span>
                <div className="sp-card__head-text">
                  <h2 className="sp-card__title">School details</h2>
                  <p className="sp-card__sub">Base identity used across the ERP</p>
                </div>
              </header>
              <div className="sp-compact__grid">
                <CompactField label="School code" value={school.school_code} />
                <CompactField label="Est. year" value={school.school_estb_year} />
                <CompactField label="Reg. no." value={school.school_reg_no} />
                <CompactField label="UDISE no." value={school.udise_no} />
                <CompactField
                  label="Contact 1"
                  value={formatPhone(school.officail_contact_no1)}
                />
                <CompactField
                  label="Contact 2"
                  value={formatPhone(school.officail_contact_no2)}
                />
                <CompactField
                  label="Email"
                  value={school.officail_email}
                  href={
                    school.officail_email
                      ? `mailto:${school.officail_email}`
                      : undefined
                  }
                />
                <CompactField
                  label="Website"
                  value={school.website}
                  href={websiteHref(school.website) || undefined}
                />
                <CompactField label="District" value={school.udise_district} />
                <CompactField label="Block" value={school.udise_block} />
                <CompactField label="State" value={school.state} />
                <CompactField label="Country" value={school.country} />
                <div className="sp-compact__item sp-compact__item--wide">
                  <span className="sp-compact__label">Tag line</span>
                  <span
                    className={`sp-compact__value${
                      displayValue(school.school_tag_line) === "—"
                        ? " sp-compact__value--empty"
                        : ""
                    }`}
                  >
                    {displayValue(school.school_tag_line)}
                  </span>
                </div>
                <div className="sp-compact__item sp-compact__item--wide">
                  <span className="sp-compact__label">Address</span>
                  <span
                    className={`sp-compact__value${
                      displayValue(school.school_address) === "—"
                        ? " sp-compact__value--empty"
                        : ""
                    }`}
                  >
                    {displayValue(school.school_address)}
                  </span>
                </div>
              </div>
            </section>

            <section className="sp-card sp-aff-section sp-reveal sp-reveal--delay">
              <header className="sp-card__head">
                <span className="sp-card__icon sp-card__icon--amber" aria-hidden>
                  <FaLayerGroup />
                </span>
                <div className="sp-card__head-text">
                  <h2 className="sp-card__title">Affiliations</h2>
                  <p className="sp-card__sub">
                    Wings with their own name, number &amp; class map
                  </p>
                </div>
                <button
                  type="button"
                  className="sp-aff-section__link"
                  onClick={() => history.push("/AdminMain/SchoolAffiliations")}
                >
                  Manage
                </button>
              </header>
              <div className="sp-card__body sp-aff-section__body">
                {affiliations.length === 0 ? (
                  <div className="sp-empty sp-empty--tight">
                    No affiliations for the current session. Add wings (e.g. Play–V,
                    VI–VIII, IX–XII) with their own affiliation number and school
                    details.
                  </div>
                ) : (
                  <div className="sp-aff-list">
                    {affiliations.map((a) => (
                      <article
                        key={a.affiliation_id}
                        className={`sp-aff-row${
                          Number(a.is_primary) === 1 ? " is-primary" : ""
                        }`}
                      >
                        <div className="sp-aff-row__top">
                          <div className="sp-aff-row__identity">
                            {a.school_logo_path ? (
                              <img
                                src={a.school_logo_path}
                                alt=""
                                className="sp-aff-row__logo"
                              />
                            ) : (
                              <span
                                className="sp-aff-row__logo sp-aff-row__logo--fallback"
                                aria-hidden
                              >
                                <FaSchool />
                              </span>
                            )}
                            <span className="sp-aff-row__title">
                              {a.affiliation_name ||
                                a.school_name ||
                                "Affiliation"}
                              {Number(a.is_primary) === 1 ? (
                                <span className="sp-aff-badge">
                                  <FaStar aria-hidden /> Primary
                                </span>
                              ) : null}
                            </span>
                          </div>
                        </div>
                        {a.school_name ? (
                          <span className="sp-aff-row__school">{a.school_name}</span>
                        ) : null}
                        <span className="sp-aff-row__meta">
                          {a.affiliation_no
                            ? `Affiliation no. ${a.affiliation_no}`
                            : "No affiliation number"}
                          {" · "}
                          {a.class_count || 0} class
                          {Number(a.class_count) === 1 ? "" : "es"}
                        </span>
                        {a.class_names ? (
                          <div className="sp-aff-row__class-wrap">
                            {String(a.class_names)
                              .split(",")
                              .map((name) => name.trim())
                              .filter(Boolean)
                              .map((name) => (
                                <span key={name} className="sp-aff-chip">
                                  {name}
                                </span>
                              ))}
                          </div>
                        ) : (
                          <span className="sp-aff-row__classes sp-aff-row__classes--empty">
                            No classes mapped
                          </span>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <div className="sp-actions sp-reveal sp-reveal--delay2">
              <button type="button" className="sp-btn sp-btn--primary" onClick={handleEdit}>
                <FaEdit aria-hidden />
                Edit school profile
              </button>
              <button
                type="button"
                className="sp-btn sp-btn--ghost"
                onClick={() => history.push("/AdminMain/SchoolAffiliations")}
              >
                <FaLayerGroup aria-hidden />
                Manage affiliations
              </button>
              <button type="button" className="sp-btn sp-btn--ghost" onClick={handleShare}>
                <FaShareAlt aria-hidden />
                Share profile
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
